import { useEffect, useState } from "react";

import Logging from "$src/core/services/logging/logging";
import SearchClassification from "$src/core/services/search-classification";
import type { ClassificationResult } from "$src/core/services/search-classification/types";
import { isLanguageSupported } from "$src/core/services/search-classification/utils";

const useSearchClassification = (
  query?: string | null,
  { locale, enabled = false }: { locale?: string; enabled?: boolean } = {},
) => {
  const [status, setStatus] = useState<
    "idle" | "pending" | "success" | "error"
  >("idle");
  const [classification, setClassification] =
    useState<ClassificationResult | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  /* Classify whenever the enabled state / query / locale / retry count changes */
  useEffect(() => {
    let cancelled = false;

    if (!enabled || !isLanguageSupported(locale) || !query) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setClassification(null);
      setStatus("idle");
    } else {
      setStatus("pending");

      SearchClassification.classify(query, { locale })
        .then((intent) => {
          if (cancelled) return;
          setClassification(intent);
          setStatus("success");
        })
        .catch((error) => {
          if (cancelled) return;
          Logging.warn("Failed to classify search query", { query, error });
          setClassification(null);
          setStatus("error");
        });
    }

    return () => {
      cancelled = true;
    };
  }, [query, locale, enabled, retryCount]);

  return {
    status,
    classification,
    retry: () => setRetryCount((count) => count + 1),
  };
};

export default useSearchClassification;
